import { navigate } from 'gatsby';
import { or } from 'ramda';
import React, { FC, useMemo } from 'react';
import { ToastContainer } from 'react-toastify';
import { toast } from 'react-toastify';
import { useDeepCompareEffect } from 'react-use';
import styled from 'styled-components';

import BasicText from '@ui/atoms/BasicText';
import FlexColumn, { IFlexColumn } from '@ui/atoms/FlexColumn';
import HeadingText from '@ui/atoms/HeadingText';
import SelectableButtonGrid, { ISelectableButtonGrid } from '@ui/atoms/SelectableButtonGrid';
import SubheadingText from '@ui/atoms/SubheadingText';

import DashboardCards from '@ui/molecules/DashboardCards';

import PlanCard from '@ui/organisms/PlanCard';
import ProfileDropdown from '@ui/organisms/ProfileDropdown';

import fullWidth from '@ui/styled/FullWidth';

import { ILegalName, MemberInfo, SubscriptionInfo } from '@hooks/useMemberInfo';
import useWindowSize from '@hooks/useWindowSize';

import { interpolate } from '@utils/butter';
import { doesNotExist } from '@utils/general';

import { CardContent } from '@folx-types/dashboard';
import { IPageProps } from '@folx-types/flow-steps';
import { IActiveSubscriptionRx } from '@folx-types/medications';

import theme from '@theme/FolxTheme';

import {
	appointments,
	billing,
	community,
	getCare,
	helpCenter,
	labs,
	library,
	messages,
} from './QuickLinks';
import TasksSection from './Tasks/TasksSection';

export interface IMemberHomeStepPageContent {
	pageHeader: string;
	billingHeader: string;
	billingDescription: string;
	appointmentsHeader: string;
	appointmentsDescription: string;
	labsHeader: string;
	labsDescription: string;
	innerspaceHeader: string;
	innerspaceDescription: string;
	libraryHeader: string;
	libraryDescription: string;
	helpHeader: string;
	helpDescription: string;
	messagesHeader: string;
	getCareHeader: string;
	homeHeader: string;
	accountHeader: string;
	dependentsHeader: string;
	logoutHeader: string;
	memberHomeToast: {
		message: string;
		link: string;
		showToast: boolean;
	};
}

interface IMemberHomeStepProps extends IPageProps {
	activePrescriptions: IActiveSubscriptionRx[];
	pageContent: IMemberHomeStepPageContent;
	member?: MemberInfo & SubscriptionInfo & ILegalName;
}

const FullWidthGrid = fullWidth<ISelectableButtonGrid>(SelectableButtonGrid);
const FullWidthColumn = fullWidth<IFlexColumn>(FlexColumn);

const SectionHeader = styled(SubheadingText)`
	grid-column-start: span 2;
	grid-column-end: auto;
	margin-top: 3rem;
`;

const CenteredText = styled(BasicText)`
	text-align: center;
`;

const StyledToastContainer = styled(ToastContainer)`
	.Toastify__toast {
		cursor: pointer;
	}
`;

const toastBreakpoint = '481px';

const MemberHomeStep: FC<IMemberHomeStepProps> = ({ activePrescriptions, member, pageContent }) => {
	const {
		pageHeader,
		billingHeader,
		billingDescription,
		appointmentsHeader,
		appointmentsDescription,
		labsHeader,
		labsDescription,
		innerspaceHeader,
		innerspaceDescription,
		libraryHeader,
		libraryDescription,
		helpHeader,
		helpDescription,
		getCareHeader,
		messagesHeader,
		memberHomeToast,
	} = pageContent;

	const { isMobile } = useWindowSize(toastBreakpoint);

	useDeepCompareEffect(() => {
		if (doesNotExist(member) || !memberHomeToast.showToast) {
			return;
		}

		toast(<CenteredText color="white">{memberHomeToast.message}</CenteredText>);

		const noMemberName = or(
			doesNotExist(member?.legalFirstName),
			doesNotExist(member?.legalLastName)
		);
		noMemberName && navigate('./name_edit');
	}, [member]);

	const getCareCardContents: Array<CardContent> = useMemo(() => {
		const cards = [getCare({ header: getCareHeader }), messages({ header: messagesHeader })];
		return cards;
	}, []);

	const quickLinksCardContents: Array<CardContent> = useMemo(() => {
		const cards = [
			billing({ header: billingHeader, description: billingDescription }),
			appointments({ header: appointmentsHeader, description: appointmentsDescription }),
			library({ header: libraryHeader, description: libraryDescription }),
			community({ header: innerspaceHeader, description: innerspaceDescription }),
			labs({ header: labsHeader, description: labsDescription }),
			helpCenter({ header: helpHeader, description: helpDescription }),
		];
		return cards;
	}, []);

	const header = interpolate(pageHeader, { firstName: member?.displayNameFirst ?? 'back' });

	return (
		<FullWidthColumn gap="sm">
			{memberHomeToast.showToast && (
				<StyledToastContainer
					position={isMobile ? 'top-center' : 'top-right'}
					theme="dark"
					autoClose={false}
					onClick={() => {
						navigate(memberHomeToast.link);
					}}
				/>
			)}
			<ProfileDropdown />
			<HeadingText>{header}</HeadingText>
			<FullWidthGrid
				mobileColumns={2}
				desktopColumns={2}
				columnGap="sm"
				rowGap="sm"
				breakpointRowGap="sm"
				breakpoint="tablet">
				<SectionHeader>Get care</SectionHeader>
				<DashboardCards variant="minimal" cards={getCareCardContents} />
				<PlanCard activePrescriptions={activePrescriptions} member={member} />
			</FullWidthGrid>
			<TasksSection />
			<SectionHeader>Quick links</SectionHeader>
			<FullWidthGrid
				mobileColumns={1}
				desktopColumns={3}
				columnGap="sm"
				rowGap="xssm"
				breakpointRowGap="lg"
				breakpoint="tablet">
				<DashboardCards cards={quickLinksCardContents} />
			</FullWidthGrid>
		</FullWidthColumn>
	);
};

export default MemberHomeStep;
